import React, { useCallback } from "react";
import { useFormikContext } from "formik";
import { SlideoutDrawerV2, Button, OverlayStyle } from "@jsq/ds/core";
import { Form } from "@jsq/ds/forms";
import { toGlobalId } from "#utils/graphql_utils";
import { getArenaEntityUrl } from "#utils/url_utils";
import { AccountDetailsSection } from "./AccountDetailsSection";
import { ConfigurationSection } from "./ConfigurationSection/index";
import { EntityAssociationModal } from "./EntityAssociationModal";
import {
  slideoutContainer,
  formWrapper,
  slideoutBody,
  slideoutFooter,
} from "./styles_vanilla.css";
import {
  BankAccountConfigurationSlideoutProps,
  bankAccountConfigurationSchema,
  ProviderBankSource,
  SlideoutFormState,
} from "./types";
import { useBankAccountConfiguration } from "./useBankAccountConfiguration";
import type { InvestmentEntity } from "../types";

interface FormContentProps {
  bankAccount: NonNullable<BankAccountConfigurationSlideoutProps["bankAccount"]>;
  reportingEntityOverride: InvestmentEntity | null;
  onClose: () => void;
  handleOpenEntityModal: () => void;
  fidesBankSources: ProviderBankSource[];
  rpaBankSources: ProviderBankSource[];
  isLoadingProviders: boolean;
  isSaving: boolean;
  isEntityModalOpen: boolean;
  handleEntityModalClose: () => void;
  handleEntityModalSave: (entityId: number | null, entityName: string) => Promise<boolean>;
  isEntityModalSaving: boolean;
  arenaId?: number | null;
  getEntityUrl?: (entityId: number) => string | undefined;
}

const FormContent: React.FC<FormContentProps> = ({
  bankAccount,
  reportingEntityOverride,
  onClose,
  handleOpenEntityModal,
  fidesBankSources,
  rpaBankSources,
  isLoadingProviders,
  isSaving,
  isEntityModalOpen,
  handleEntityModalClose,
  handleEntityModalSave,
  isEntityModalSaving,
  arenaId,
  getEntityUrl,
}) => {
  const { values, errors, setFieldValue, submitForm } = useFormikContext<SlideoutFormState>();

  return (
    <div className={formWrapper}>
      <div className={slideoutBody}>
        <AccountDetailsSection
          bankAccount={bankAccount}
          onEditEntity={handleOpenEntityModal}
          getEntityUrl={getEntityUrl}
          reportingEntityOverride={reportingEntityOverride}
        />
        <ConfigurationSection
          fidesEnabled={values.fidesEnabled}
          onFidesEnabledChange={(enabled) => void setFieldValue("fidesEnabled", enabled)}
          fidesBankMapping={values.fidesBankMapping}
          onFidesBankMappingChange={(value) => void setFieldValue("fidesBankMapping", value)}
          fidesVerificationStatus={values.fidesVerificationStatus}
          onFidesVerificationChange={(value) =>
            void setFieldValue("fidesVerificationStatus", value)
          }
          fidesBankSources={fidesBankSources}
          fidesBankMappingError={errors.fidesBankMapping}
          rpaEnabled={values.rpaEnabled}
          onRpaEnabledChange={(enabled) => void setFieldValue("rpaEnabled", enabled)}
          rpaBankMapping={values.rpaBankMapping}
          onRpaBankMappingChange={(value) => void setFieldValue("rpaBankMapping", value)}
          rpaBankSources={rpaBankSources}
          rpaBankMappingError={errors.rpaBankMapping}
          fundRecsEnabled={values.fundRecsEnabled}
          onFundRecsEnabledChange={(enabled) => void setFieldValue("fundRecsEnabled", enabled)}
          fundRecsUdf={values.fundRecsUdf}
          onFundRecsUdfChange={(value) => void setFieldValue("fundRecsUdf", value)}
          fundRecsUdfError={errors.fundRecsUdf}
          fundRecsEnabledError={errors.fundRecsEnabled}
          isLoadingProviders={isLoadingProviders}
        />
      </div>
      <div className={slideoutFooter}>
        <Button variant="neutral" onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button variant="primary" onClick={() => void submitForm()} isLoading={isSaving}>
          Save
        </Button>
      </div>
      <EntityAssociationModal
        isOpen={isEntityModalOpen}
        onClose={handleEntityModalClose}
        currentEntityId={values.selectedEntityId}
        currentEntityName={reportingEntityOverride?.entityName ?? null}
        arenaId={arenaId ? toGlobalId("ArenaObjectType", arenaId) : undefined}
        onSave={async (entityId, entityName) => {
          const success = await handleEntityModalSave(entityId, entityName);
          if (success) {
            void setFieldValue("selectedEntityId", entityId);
          }
        }}
        isSaving={isEntityModalSaving}
      />
    </div>
  );
};

export const BankAccountConfigurationSlideout: React.FC<
  BankAccountConfigurationSlideoutProps
> = ({ isOpen, onClose, bankAccount, onSave, onEntitySave, arenaId, arenaDomain }) => {
  const {
    initialFormValues,
    localMappedEntity,
    isEntityModalOpen,
    handleOpenEntityModal,
    handleEntityModalClose,
    handleEntityModalSave,
    isEntityModalSaving,
    fidesBankSources,
    rpaBankSources,
    isSaving,
    isLoadingProviders,
    handleSave,
  } = useBankAccountConfiguration({
    bankAccount,
    arenaId,
    onSave,
    onEntitySave,
    onClose,
  });

  const getEntityUrl = useCallback(
    (entityId: number): string | undefined => {
      if (!arenaDomain) return undefined;
      return getArenaEntityUrl(arenaDomain, entityId);
    },
    [arenaDomain],
  );

  if (!bankAccount) return null;

  return (
    <SlideoutDrawerV2
      overlayStyle={OverlayStyle.Transparent}
      isOpen={isOpen}
      onClose={onClose}
      data-testid="bank-account-configuration-slideout"
    >
      <div className={slideoutContainer}>
        <SlideoutDrawerV2.ComplexHeader
          title={bankAccount.bankAccountName || `Bank Account ${bankAccount.bankAccountId}`}
        />
        <div className={formWrapper}>
          <Form<SlideoutFormState>
            initialValues={initialFormValues}
            yupValidationSchema={bankAccountConfigurationSchema}
            enableReinitialize
            validateOnChange={false}
            validateOnBlur={false}
            onSubmit={(values) => void handleSave(values as SlideoutFormState)}
          >
            <FormContent
              bankAccount={bankAccount}
              reportingEntityOverride={localMappedEntity}
              onClose={onClose}
              handleOpenEntityModal={handleOpenEntityModal}
              fidesBankSources={fidesBankSources}
              rpaBankSources={rpaBankSources}
              isLoadingProviders={isLoadingProviders}
              isSaving={isSaving}
              isEntityModalOpen={isEntityModalOpen}
              handleEntityModalClose={handleEntityModalClose}
              handleEntityModalSave={handleEntityModalSave}
              isEntityModalSaving={isEntityModalSaving}
              arenaId={arenaId}
              getEntityUrl={getEntityUrl}
            />
          </Form>
        </div>
      </div>
    </SlideoutDrawerV2>
  );
};

export default BankAccountConfigurationSlideout;
